/**
 * Canonical DOM predicates about an element's "editability".
 *
 * PATTERN TO FOLLOW: the question "is this element editable?" is answered ONLY
 * from here. This logic (`TAGS.has(tagName) || isContentEditable`)
 * used to be copied in `lib/gestures/guards`, `lib/interaction/context`,
 * `components/ui/dialog` and `todo-detail.helpers` with four slightly different
 * variants, risking divergence. Import these helpers instead of rewriting
 * the check.
 *
 * There are two predicates with DISTINCT semantics — choose according to intent:
 *  - `isEditableElement`: any form control (including SELECT,
 *    checkbox…) or `contenteditable`. Use it to *suppress* gestures/shortcuts
 *    globally when focus is inside a control.
 *  - `isTextEntryElement`: ONLY free-form text entry (textarea or textual
 *    input). Stricter. Use it for decisions based on the text cursor,
 *    e.g. not stealing arrow keys while editing text.
 *
 * Note: some checks (e.g. in the detail's key handling) intentionally use
 * their own tag sets (exclude SELECT, include BUTTON/A…) because they ask a
 * different question ("does the control consume this key?"). Those
 * should NOT be collapsed here: the distinct semantics are intentional.
 */

/** Form controls that count as "editable". */
const FORM_CONTROL_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

/** `<input>` types that accept free-form text entry with a cursor. */
const TEXT_INPUT_TYPES = new Set(["text", "search", "url", "tel", "email", "password", "number"]);

/**
 * `true` if `el` is a form control (input/textarea/select) or a
 * `contenteditable` element.
 */
export function isEditableElement(el: Element | null): el is HTMLElement {
  return el instanceof HTMLElement && (FORM_CONTROL_TAGS.has(el.tagName) || el.isContentEditable);
}

/**
 * `true` if `el` accepts free-form text entry: a `<textarea>` or a textual
 * `<input>`. Stricter than {@link isEditableElement} (excludes SELECT,
 * checkbox, radio, etc.).
 */
export function isTextEntryElement(
  el: Element | null,
): el is HTMLTextAreaElement | HTMLInputElement {
  if (el instanceof HTMLTextAreaElement) {
    return true;
  }
  if (el instanceof HTMLInputElement) {
    return TEXT_INPUT_TYPES.has(el.type.toLowerCase());
  }
  return false;
}

/**
 * `true` if any editable field has focus, or any `contenteditable` exists
 * in the document. Intended for global gates (dragging dialogs, navigation
 * swipes) that must yield to editing. SSR-safe.
 */
export function isEditingActive(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  if (document.querySelector('[contenteditable="true"], [contenteditable=""]') !== null) {
    return true;
  }
  return isEditableElement(document.activeElement);
}
